import { useCallback, useMemo, useState } from "react";
import { Divider, Header, Message, Segment } from "semantic-ui-react";
import "react-datepicker/dist/react-datepicker.css";
import { useFlags } from "launchdarkly-react-client-sdk";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import CreativesService from "../../../services/creatives";
import { default_button_text } from "../fixtures";
import CreativeDisplayForm from "../form/display";
import CreativeVideoForm from "../form/video";
import { CREATIVE_TYPES } from "../index/constants";

const initialData = {
  // biome-ignore lint/style/useNamingConvention: <server params>
  advertiser_id: null,

  // biome-ignore lint/style/useNamingConvention: <server params>
  clickthrough_url: "",

  // biome-ignore lint/style/useNamingConvention: <server params>
  landing_page_url: "",
  status: 1,
  title: "",

  // biome-ignore lint/style/useNamingConvention: <server params>
  end_date: "",

  // biome-ignore lint/style/useNamingConvention: <server params>
  start_date: "",
};

const generateBackUrl = ({ isVideo, isBrandLiftVideo, isDisplay, isBrandLift }) => {
  let backUrl = "";
  if (isVideo) {
    backUrl = "/creatives/video";
  } else if (isBrandLiftVideo || isBrandLift) {
    backUrl = "/creatives/brand-lift";
  } else if (isDisplay) {
    backUrl = "/creatives/display";
  }
  return backUrl;
};

const buildInitialData = (
  data,
  { isVideo, isBrandLiftVideo, isBrandLift, assetRestriction, whitelabel1PDisplayCreative, intl },
) => {
  const initialData = {};
  if (isVideo || isBrandLiftVideo) {
    Object.assign(initialData, {
      // biome-ignore lint/style/useNamingConvention: <server params>
      ad_title: "",

      // biome-ignore lint/style/useNamingConvention: <server params>
      brand_name: "",

      // biome-ignore lint/style/useNamingConvention: <server params>
      button_color: "#f2711c",

      // biome-ignore lint/style/useNamingConvention: <server params>
      button_text_type: "standard",

      // biome-ignore lint/style/useNamingConvention: <server params>
      button_text: default_button_text(intl)[0].value,

      // biome-ignore lint/style/useNamingConvention: <server params>
      companion_ad: 0,

      // biome-ignore lint/style/useNamingConvention: <server params>
      custom_vast_url: "",

      // biome-ignore lint/style/useNamingConvention: <server params>
      deep_link: "",

      // biome-ignore lint/style/useNamingConvention: <server params>
      end_date: "",

      // biome-ignore lint/style/useNamingConvention: <server params>
      external_identifier: "",

      // biome-ignore lint/style/useNamingConvention: <server params>
      logo_url: "",

      // biome-ignore lint/style/useNamingConvention: <server params>
      third_party: 0,

      instoreDOOH: 0,

      // biome-ignore lint/style/useNamingConvention: <server params>
      ...(isBrandLiftVideo
        ? { tracking_url: "", brand_lift: 1, creative_type: CREATIVE_TYPES.brandLift, status: 1, campaign_id: null }
        : { creative_type: CREATIVE_TYPES.offsite }),
    });
  } else {
    Object.assign(initialData, {
      // biome-ignore lint/style/useNamingConvention: <server params>
      ad_format: "",

      // biome-ignore lint/style/useNamingConvention: <server params>
      ad_server: "",

      dimension: "standard",
      dimensions: "",

      // biome-ignore lint/style/useNamingConvention: <server params>
      end_date: "",

      // biome-ignore lint/style/useNamingConvention: <server params>
      expansion_direction: "",

      // biome-ignore lint/style/useNamingConvention: <server params>
      expansion_trigger: "",

      // biome-ignore lint/style/useNamingConvention: <server params>
      file_type: "",

      // biome-ignore lint/style/useNamingConvention: <server params>
      is_expandable: 0,

      // biome-ignore lint/style/useNamingConvention: <server params>
      mraid_type: "",
      mraid: 0,

      // biome-ignore lint/style/useNamingConvention: <server params>
      tag_type: "",
      tag: "",

      // biome-ignore lint/style/useNamingConvention: <server params>
      third_party: !assetRestriction || whitelabel1PDisplayCreative ? 0 : 1,

      // biome-ignore lint/style/useNamingConvention: <server params>
      tpas_tag_id: "",

      ...(isBrandLift
        ? { brand_lift: 1, creative_type: CREATIVE_TYPES.brandLift, status: 1, campaign_id: null }
        : { creative_type: CREATIVE_TYPES.offsite }),
    });
  }
  return { ...data, ...initialData };
};

/**
 * Creative create page component
 * @param history
 * @param {string} creative_type
 * @return {JSX.Element}
 * @constructor
 */
const CreativeCreatePage = ({ history, creative_type }) => {
  const [serverError, setServerError] = useState("");
  const [loading, setLoading] = useState(false);

  const intl = useIntl();
  const service = new CreativesService();
  const isVideo = creative_type === "video";
  const isDisplay = creative_type === "display";
  const { whitelabel1PDisplayCreative, whitelabel3PDisplayCreative, whitelabelDoubleCreativeApproval } = useFlags();
  const assetRestriction = !(whitelabel1PDisplayCreative && whitelabel3PDisplayCreative);
  const isBrandLiftVideo = creative_type === CREATIVE_TYPES.brandLiftVideo;
  const isBrandLift = creative_type === CREATIVE_TYPES.brandLift;
  const backUrl = generateBackUrl({ isVideo, isBrandLiftVideo, isDisplay, isBrandLift });

  const computedInitData = useMemo(
    () =>
      buildInitialData(initialData, {
        isVideo,
        isBrandLiftVideo,
        isBrandLift,
        assetRestriction,
        whitelabel1PDisplayCreative,
        intl,
      }),
    [isVideo, isBrandLiftVideo, isBrandLift, assetRestriction, whitelabel1PDisplayCreative, intl],
  );

  /**
   * navigate user back
   */
  const handleCancel = useCallback(() => {
    if (backUrl) {
      history.push(backUrl);
      return;
    }
    history.goBack();
  }, [backUrl, history]);

  /**
   * check if creative type requires double approval
   * @param {string} creativeType
   * @returns {boolean}
   */
  const requiresDoubleApproval = (creativeType) => {
    const doubleApprovalCreatives = [CREATIVE_TYPES.onsite, CREATIVE_TYPES.instoreDOOH, CREATIVE_TYPES.brandLift];
    return whitelabelDoubleCreativeApproval && doubleApprovalCreatives.includes(creativeType);
  };

  /**
   * form submit handler
   * @param {object} values
   * @param {File} file
   */
  const handleSubmit = async (values, file, creativeType) => {
    let redirectUrl = "";
    setLoading(true);

    const form = new FormData();
    for (const k of Object.keys(values)) {
      form.append(k, values[k]);
    }

    if (file) {
      form.append("creative_file", file);
    }

    try {
      if (requiresDoubleApproval(creativeType)) {
        const creativePaths = {
          [CREATIVE_TYPES.instoreDOOH]: "/creatives/in-store-dooh",
          [CREATIVE_TYPES.onsite]: "/creatives/onsite",
          [CREATIVE_TYPES.brandLift]: "/creatives/brand-lift",
        };
        redirectUrl = creativePaths[creativeType];
        await service.createLocal(form);
      } else if (isVideo) {
        redirectUrl = "/creatives/video";
        await service.createVideo(form);
      } else {
        redirectUrl = "/creatives/display";
        await service.createDisplay(form);
      }

      history.push(redirectUrl, { action: "created" });
    } catch (e) {
      setServerError(e.error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Segment basic>
      <Header as="h2">
        {isVideo &&
          intl.formatMessage({
            defaultMessage: "Create Video Creative",
            id: "HEADING_CREATE_VIDEO_CREATIVE",
          })}
        {isDisplay &&
          intl.formatMessage({
            defaultMessage: "Create Display Creative",
            id: "HEADING_CREATE_DISPLAY_CREATIVE",
          })}
        {isBrandLiftVideo &&
          intl.formatMessage({
            defaultMessage: "Create Brand Lift Video Creative",
            id: "HEADING_CREATE_BRAND_LIFT_VIDEO_CREATIVE",
          })}
        {isBrandLift &&
          intl.formatMessage({
            defaultMessage: "Create Brand Lift Display Creative",
            id: "HEADING_CREATE_BRAND_LIFT_DISPLAY_CREATIVE",
          })}
      </Header>
      <Divider />
      <Message
        content={serverError}
        error
        hidden={!serverError}
        size="tiny"
        style={{ marginTop: "10px" }}
      />
      {isVideo || isBrandLiftVideo ? (
        <CreativeVideoForm
          initialData={computedInitData}
          isBrandLift={isBrandLiftVideo}
          loading={loading}
          onCancel={handleCancel}
          onsite_enabled={whitelabelDoubleCreativeApproval}
          onSubmit={handleSubmit}
          submitText={intl.formatMessage({
            defaultMessage: "Create Creative",
            id: "BTN_CREATE_CREATIVE",
          })}
        />
      ) : (
        <CreativeDisplayForm
          assetRestriction={assetRestriction}
          initialData={computedInitData}
          isBrandLift={isBrandLift}
          loading={loading}
          onCancel={handleCancel}
          onsite_enabled={whitelabelDoubleCreativeApproval}
          onSubmit={handleSubmit}
          submitText={intl.formatMessage({
            defaultMessage: "Create Creative",
            id: "BTN_CREATE_CREATIVE",
          })}
        />
      )}
    </Segment>
  );
};

CreativeCreatePage.propTypes = {
  creative_type: PropTypes.string.isRequired,
  history: PropTypes.object.isRequired,
};

export default CreativeCreatePage;
